import { useState } from "react";
import { RefreshCcw, ArrowRight, Camera, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PhotoThumb } from "@/components/Common";
import PhotoUploader from "@/components/PhotoUploader";
import { Question } from "@/components/analysis/AnamnesisStep";

const MAX = 3;

function ExtraPhotos({ a, reload }) {
  const [open, setOpen] = useState(false);
  const extra = (a.photos || []).filter((p) => p.phase === "reassess");
  const remove = async (id) => {
    await api.delete(`/photos/${id}`).catch((e) => toast.error(errMsg(e)));
    reload();
  };
  return (
    <div data-testid="reassess-photos">
      {extra.length > 0 && (
        <div className="mb-3 flex gap-2">
          {extra.map((p) => (
            <div key={p.id} className="relative">
              <PhotoThumb id={p.id} className="h-20 w-20" testid={`reassess-photo-${p.id}`} />
              <button onClick={() => remove(p.id)} className="absolute -right-2 -top-2 rounded-full bg-[#0D2517] p-1 text-white" aria-label="Rimuovi foto" data-testid={`reassess-photo-remove-${p.id}`}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      {extra.length < MAX && (open ? (
        <PhotoUploader analysisId={a.id} kind="dettaglio" requestId="reassess" max={MAX - extra.length} compact onUploaded={reload} testid="reassess-uploader" />
      ) : (
        <button onClick={() => setOpen(true)} className="btn-secondary min-h-[48px] w-full bg-white !text-base" data-testid="reassess-add-photos-button">
          <Camera size={18} /> Carica altre foto
        </button>
      ))}
      <p className="mt-2 text-[15px] text-[#5b6b60]" data-testid="reassess-photo-count">Foto aggiunte: {extra.length} di {MAX}</p>
    </div>
  );
}

export default function ReassessPanel({ a, reload }) {
  const rs = a.reassess;
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const nPhotos = (a.photos || []).filter((p) => p.phase === "reassess").length;
  const hasInfo = nPhotos > 0 || notes.trim() || Object.values(answers).some((v) => v && v !== "Non so");

  const post = async (path, body) => {
    setBusy(true);
    await api.post(`/analyses/${a.id}/${path}`, body).catch((e) => toast.error(errMsg(e)));
    setBusy(false);
    reload();
  };

  return (
    <div className="mt-5 space-y-4 border-t border-[#ddc69c]/60 pt-4" data-testid="reassess-panel">
      <p className="text-[17px] leading-relaxed text-[#2D3A30]">Puoi aggiungere foto e informazioni <strong>una sola volta</strong>: la diagnosi verrà rivalutata insieme ai dati già forniti, senza alcun costo aggiuntivo.</p>
      <ExtraPhotos a={a} reload={reload} />
      {rs.questions?.map((q) => <Question key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswers({ ...answers, [q.id]: v })} />)}
      <label className="block">
        <span className="text-[17px] font-semibold text-[#0D2517]">Hai notato altri sintomi? Descrivili qui</span>
        <textarea className="field mt-2 min-h-[80px]" maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Facoltativo" data-testid="reassess-notes" />
      </label>
      <button onClick={() => post("reassess", { answers, notes })} disabled={busy || !hasInfo} className="btn-primary min-h-[48px] w-full !text-base" data-testid="reassess-submit-button">
        <RefreshCcw size={16} /> Rivaluta la diagnosi
      </button>
      {!hasInfo && <p className="text-center text-[15px] text-[#5b6b60]" data-testid="reassess-hint">Aggiungi almeno una foto, una risposta o una descrizione.</p>}
      <button onClick={() => post("reassess/skip")} disabled={busy} className="btn-secondary min-h-[48px] w-full bg-white !text-base" data-testid="reassess-skip-button">
        Prosegui senza aggiungere informazioni <ArrowRight size={16} />
      </button>
    </div>
  );
}
